/**
 * Small, drawn-in-code previews of real Voucha placements for the /advertise and /business pages.
 * Purely illustrative (aria-hidden): no figures, just the shape of each placement.
 */

const line = (w, cls = '') => m("span", {
  className: 'VAPrev-line ' + cls,
  style: {
    width: w
  }
});
const avatar = (cls = '') => m("span", {
  className: 'VAPrev-avatar ' + cls
});
const brandHead = (label = 'Sponsored') => m("div", {
  className: "VAPrev-head"
}, m("span", {
  className: "VAPrev-logo"
}), m("span", {
  className: "VAPrev-who"
}, m("strong", null, "Your brand"), m("small", null, label)));

/** An ordinary post, greyed out, so the sponsored one reads as "in the feed" */
const fadedPost = () => m("div", {
  className: "VAPrev-post is-faded"
}, avatar(), m("div", {
  className: "VAPrev-stack"
}, line('70%', 'is-strong'), line('92%')));

/** A sponsored post between two community posts */
export const FeedPreview = {
  view: () => m("div", {
    className: "VAPrev VAPrev--feed",
    "aria-hidden": "true"
  }, fadedPost(), m("div", {
    className: "VAPrev-post is-sponsored"
  }, m("div", {
    className: "VAPrev-stack"
  }, brandHead(), m("div", {
    className: "VAPrev-title"
  }, "Your message, in front of the right readers"), m("div", {
    className: "VAPrev-text"
  }, "Clearly labelled and placed where readers are making decisions."), m("span", {
    className: "VAPrev-cta"
  }, "Learn more"))), fadedPost())
};

/** A sponsored Ask Me Anything thread with community replies */
export const ThreadPreview = {
  view: () => m("div", {
    className: "VAPrev VAPrev--thread",
    "aria-hidden": "true"
  }, m("div", {
    className: "VAPrev-tags"
  }, m("span", {
    className: "VAPrev-tag is-brand"
  }, "AMA"), m("span", {
    className: "VAPrev-tag"
  }, "Sponsored")), m("div", {
    className: "VAPrev-title"
  }, "Ask our team anything about moving money home"), m("div", {
    className: "VAPrev-reply"
  }, avatar('is-a'), m("div", {
    className: "VAPrev-bubble"
  }, line('85%'), line('55%'))), m("div", {
    className: "VAPrev-reply is-answer"
  }, m("span", {
    className: "VAPrev-logo is-small"
  }), m("div", {
    className: "VAPrev-bubble is-brand"
  }, line('90%'), line('70%'), line('40%'))), m("div", {
    className: "VAPrev-reply"
  }, avatar('is-b'), m("div", {
    className: "VAPrev-bubble"
  }, line('65%'))))
};

/** A newsletter issue with a sponsored slot */
export const NewsletterPreview = {
  view: () => m("div", {
    className: "VAPrev VAPrev--mail",
    "aria-hidden": "true"
  }, m("div", {
    className: "VAPrev-mailHead"
  }, m("i", {
    className: "fas fa-envelope-open-text"
  }), m("span", null, "The Voucha newsletter")), line('80%', 'is-strong'), line('95%'), line('60%'), m("div", {
    className: "VAPrev-slot"
  }, m("small", null, "Sponsored by"), m("span", {
    className: "VAPrev-logo is-small"
  }), m("strong", null, "Your brand")))
};

/** A Data Hub chart carrying the sponsor's name */
export const ChartPreview = {
  view: ({
    attrs
  }) => m("div", {
    className: "VAPrev VAPrev--chart",
    "aria-hidden": "true"
  }, m("div", {
    className: "VAPrev-kicker"
  }, m("i", {
    className: "fas fa-chart-column"
  }), " ", attrs.kicker || 'Data Hub'), m("div", {
    className: "VAPrev-title"
  }, attrs.title || 'Inflation by state'), m("svg", {
    className: "VAPrev-bars",
    viewBox: "0 0 220 84",
    preserveAspectRatio: "none"
  }, [38, 52, 30, 66, 46, 74, 58, 44].map((h, i) => m("rect", {
    x: i * 27 + 2,
    y: 84 - h,
    width: "19",
    height: h,
    rx: "3",
    className: i === 5 ? 'is-hi' : ''
  }))), m("div", {
    className: "VAPrev-slot"
  }, m("small", null, "Sponsored by"), m("span", {
    className: "VAPrev-logo is-small"
  }), m("strong", null, "Your brand")))
};

/** A promoted business pin on the Voucha Atlas */
export const MapPreview = {
  view: () => m("div", {
    className: "VAPrev VAPrev--map",
    "aria-hidden": "true"
  }, m("svg", {
    className: "VAPrev-mapArt",
    viewBox: "0 0 240 150",
    preserveAspectRatio: "xMidYMid slice"
  }, m("path", {
    className: "VAPrev-river",
    d: "M-5 40 C 50 55, 70 95, 120 92 S 190 70, 245 118"
  }), m("path", {
    className: "VAPrev-river is-thin",
    d: "M120 92 C 128 60, 150 40, 170 -5"
  }), m("path", {
    className: "VAPrev-road",
    d: "M10 140 L 80 70 L 170 60 L 235 20"
  }), [[40, 110], [72, 40], [150, 120], [205, 85], [190, 35]].map(([x, y]) => m("circle", {
    cx: x,
    cy: y,
    r: "4",
    className: "VAPrev-dot"
  }))), m("span", {
    className: "VAPrev-pin"
  }, m("i", {
    className: "fas fa-location-dot"
  })), m("div", {
    className: "VAPrev-mapCard"
  }, m("span", {
    className: "VAPrev-logo is-small"
  }), m("span", {
    className: "VAPrev-who"
  }, m("strong", null, "Your business"), m("small", null, "Promoted on the Voucha Atlas"))))
};

/** Community partnerships: an event card with people joining */
export const PeoplePreview = {
  view: () => m("div", {
    className: "VAPrev VAPrev--people",
    "aria-hidden": "true"
  }, m("div", {
    className: "VAPrev-event"
  }, m("span", {
    className: "VAPrev-date"
  }, m("small", null, "Sat"), m("strong", null, m("i", {
    className: "fas fa-calendar-day"
  }))), m("span", {
    className: "VAPrev-who"
  }, m("strong", null, "Your brand \xD7 Voucha"), m("small", null, "Community meetup \xB7 research \xB7 hiring"))), m("div", {
    className: "VAPrev-crowd"
  }, ['is-a', 'is-b', 'is-c', 'is-d', 'is-e'].map(c => avatar(c)), m("span", {
    className: "VAPrev-going"
  }, "Members taking part")), line('90%'), line('64%'))
};

/** Pick a preview for a card from its title (falls back to the feed) */
export function previewFor(title) {
  const t = String(title || '').toLowerCase();
  if (/data|atlas|inflation|report|insight/.test(t)) return ChartPreview;
  if (/presence|profile|listing|map|business/.test(t)) return MapPreview;
  if (/partner|event|hiring|research|community/.test(t)) return PeoplePreview;
  if (/awareness|discussion|ama|ask me|brand/.test(t)) return ThreadPreview;
  if (/newsletter|email/.test(t)) return NewsletterPreview;
  return FeedPreview;
}

/** An icon for each "Who you reach" line */
export function audienceIcon(text) {
  const t = String(text || '').toLowerCase();
  if (/business|across nigeria/.test(t)) return 'fas fa-briefcase';
  if (/remit|invest|saver|money|send/.test(t)) return 'fas fa-money-bill-transfer';
  if (/move abroad|relocat|japa|preparing|planning a move/.test(t)) return 'fas fa-plane-departure';
  if (/salar|tax|cost|professional/.test(t)) return 'fas fa-scale-balanced';
  if (/canada|\bus\b|\buk\b|abroad|living/.test(t)) return 'fas fa-earth-americas';
  return 'fas fa-user-group';
}
flarum.reg.add('voucha-ads', 'forum/components/Previews', { FeedPreview: FeedPreview,ThreadPreview: ThreadPreview,NewsletterPreview: NewsletterPreview,ChartPreview: ChartPreview,MapPreview: MapPreview,PeoplePreview: PeoplePreview,previewFor: previewFor,audienceIcon: audienceIcon, });